import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons/faTrashCan';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { IconButton } from 'ui-components/components/button/icon-button';
import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { NumberInput } from 'ui-components/components/number-input';
import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { buildUomDropdownOptions, buildUomHierarchy, computeAbsoluteEaches } from '../../../../../../../constants/units-of-measure';
import { getAbsoluteEachesByUomId, toBaseQuantity, toUomQuantity } from '../../utils/uom-utils';
import { useUtcFormattedDate } from '../../../../../../../hooks/use-datetime-formats';
import { messages as sharedMessages } from '../shared-columns/messages';
import { ProductCell } from '../shared-columns/shared-columns';
import sharedStyles from '../shared-columns/shared-columns.styles.module.less';
import eachesStyles from './eaches-column.module.less';
import uomStyles from './uom-column.module.less';
import { messages } from './messages';
import styles from './styles.module.less';

import type { FlattenedProductRow } from '../../types';

type ColumnRow = {
  original: FlattenedProductRow;
};

type ProductCellWithCollapseProps = {
  row: ColumnRow;
  openProductDetails: (productId: string) => void;
  onToggleCollapse: (productId: string) => void;
};

const ProductCellWithCollapse = ({ row, openProductDetails, onToggleCollapse }: ProductCellWithCollapseProps) => {
  const { productId, rowCount, isFirstProductRow, isCollapsed } = row.original;
  const hasMultipleRows = rowCount > 1;
  const showCollapseButton = isFirstProductRow && hasMultipleRows;

  return (
    <FlexRow className={styles['product-cell-with-collapse']} gap={Gap.Space4}>
      {showCollapseButton ? (
        <IconButton
          ariaLabelMessageDescriptor={isCollapsed ? messages.expandProductAriaLabel : messages.collapseProductAriaLabel}
          icon={isCollapsed ? faChevronRight : faChevronDown}
          onClick={() => onToggleCollapse(productId)}
        />
      ) : (
        <div className={styles['collapse-button-placeholder']} />
      )}
      <ProductCell openProductDetails={openProductDetails} row={row} />
    </FlexRow>
  );
};

type ActionsCellProps = {
  row: ColumnRow;
  onRemoveRow: (productId: string, rowId: string, isCollapsed?: boolean) => void;
};

const ActionsCell = ({ row, onRemoveRow }: ActionsCellProps) => {
  const { productId, rowId, isCollapsed } = row.original;

  return (
    <FlexRow className={styles['actions-input-container']} gap={Gap.Space4}>
      <IconButton
        ariaLabelMessageDescriptor={messages.removeProductAriaLabel}
        icon={faTrashCan}
        onClick={() => onRemoveRow(productId, rowId, isCollapsed)}
      />
    </FlexRow>
  );
};

const OnHandCell = ({ row }: { row: ColumnRow }) => {
  return <Text textType={TextType.Paragraph} value={row.original.onHand?.toString() ?? '0'} />;
};

const AvailableCell = ({ row }: { row: ColumnRow }) => {
  return <Text textType={TextType.Paragraph} value={row.original.available?.toString() ?? '0'} />;
};

type UomSelectCellProps = {
  row: ColumnRow;
  onUomChange: (productId: string, rowId: string, unitOfMeasureId: number | null) => void;
};

const UomSelectCell = ({ row, onUomChange }: UomSelectCellProps) => {
  const { productId, rowId, unitsOfMeasure, unitOfMeasureId, isCollapsed } = row.original;

  if (isCollapsed || !unitsOfMeasure?.length) return null;

  const options = buildUomDropdownOptions(unitsOfMeasure);

  const hierarchyItems = options.map((o) => ({ id: o.id, label: o.label, eaches: o.eaches, parentId: o.parentId }));

  const renderOption = (option: { id?: number; label?: string; eaches?: number; parentId?: number | null; [key: string]: unknown }) => {
    const label = buildUomHierarchy(
      { id: option.id ?? 0, label: option.label ?? '', eaches: (option.eaches as number) ?? 1, parentId: option.parentId ?? null },
      hierarchyItems,
    );
    return <Text className={uomStyles['option-label']} element='span' textType={TextType.Paragraph} value={label} />;
  };

  const handleSelect = (value: string) => {
    const id = parseInt(value, 10);
    onUomChange(productId, rowId, id === 0 ? null : id);
  };

  return (
    <SearchableDropdownField
      id={`uom-select-${productId}-${rowId}`}
      isMenuFullWidth
      onItemSelected={handleSelect}
      options={options}
      renderOption={renderOption}
      required
      selectedItemValue={unitOfMeasureId != null && unitOfMeasureId !== 0 ? String(unitOfMeasureId) : '0'}
    />
  );
};

const EachesCell = ({ row }: { row: ColumnRow }) => {
  const { quantity, unitOfMeasureId, unitsOfMeasure } = row.original;
  if (unitOfMeasureId == null) return null;

  const rawUoms = unitsOfMeasure?.filter((u) => u.productUnitOfMeasureId !== 0) ?? [];
  const eaches = unitOfMeasureId === 0 ? 1 : computeAbsoluteEaches(unitOfMeasureId, rawUoms);
  const total = Math.ceil(quantity * eaches);
  return (
    <FlexColumn>
      <Text className={eachesStyles.total} element='span' textType={TextType.Paragraph} value={String(total)} />
      <Text className={eachesStyles.formula} element='span' textType={TextType.Caption} value={`(${quantity}×${eaches})`} />
    </FlexColumn>
  );
};

type UnavailableLocationProps = {
  tooltipText: string;
  displayText: string;
};

const UnavailableLocation = ({ tooltipText, displayText }: UnavailableLocationProps) => (
  <Tooltip text={tooltipText}>
    <span className={sharedStyles['location-unavailable']}>
      <FontAwesomeIcon className={sharedStyles['warning-icon']} icon={faExclamationTriangle} />
      <span className={sharedStyles['warning-text']}>{displayText}</span>
    </span>
  </Tooltip>
);

const LocationCellWithCollapse = ({ row }: { row: ColumnRow }) => {
  const intl = useIntl();
  const { location, isCollapsed, rowCount, isLocationUnavailable, hasUnavailableLocation, hasNoInventoryInWarehouse } = row.original;

  if (isCollapsed) {
    if (hasUnavailableLocation) {
      return (
        <UnavailableLocation
          displayText={intl.formatMessage(messages.multipleLocations, { count: rowCount })}
          tooltipText={intl.formatMessage(messages.multipleLocationsUnavailableTooltip)}
        />
      );
    }
    return <Text formattedMessageValues={{ count: rowCount }} textType={TextType.Paragraph} value={messages.multipleLocations} />;
  }

  if (hasNoInventoryInWarehouse) {
    return (
      <UnavailableLocation
        displayText={intl.formatMessage(messages.noInventoryInWarehouseText)}
        tooltipText={intl.formatMessage(messages.noInventoryInWarehouseTooltip)}
      />
    );
  }

  if (isLocationUnavailable) {
    return (
      <UnavailableLocation
        displayText={intl.formatMessage(messages.locationUnavailableText)}
        tooltipText={intl.formatMessage(messages.locationUnavailableTooltip)}
      />
    );
  }

  return <Text textType={TextType.Paragraph} value={location ?? ''} />;
};

const LotIdCellWithCollapse = ({ row }: { row: ColumnRow }) => {
  const { lotId, isCollapsed, combinedLotIds } = row.original;

  if (isCollapsed && combinedLotIds) {
    return <Text textType={TextType.Paragraph} value={combinedLotIds} />;
  }

  return <Text textType={TextType.Paragraph} value={lotId ?? ''} />;
};

const ExpirationDateCellWithCollapse = ({ row }: { row: ColumnRow }) => {
  const intl = useIntl();
  const { expirationDate, available, isCollapsed } = row.original;
  const formattedDate = useUtcFormattedDate(expirationDate ?? '');

  if (isCollapsed || !expirationDate) {
    return null;
  }

  const isExpired = DateTime.fromISO(expirationDate) < DateTime.now();
  const isExpiredAndUnavailable = isExpired && available === 0;

  if (isExpiredAndUnavailable) {
    return (
      <FlexColumn gap={Gap.Space4}>
        <Text textType={TextType.Paragraph} value={formattedDate} />
        <Tooltip text={intl.formatMessage(messages.expiredProductTooltip)}>
          <span className={sharedStyles['location-unavailable']}>
            <FontAwesomeIcon className={sharedStyles['warning-icon']} icon={faExclamationTriangle} />
            <span className={sharedStyles['warning-text']}>{intl.formatMessage(messages.expiredProductText)}</span>
          </span>
        </Tooltip>
      </FlexColumn>
    );
  }

  return <Text textType={TextType.Paragraph} value={formattedDate} />;
};

type QtyCellProps = {
  row: ColumnRow;
  onQuantityChange: (productId: string, rowId: string, quantity: number) => void;
};

const QtyCell = ({ row, onQuantityChange }: QtyCellProps) => {
  const { productId, rowId, quantity, available, isCollapsed, unitsOfMeasure, unitOfMeasureId } = row.original;
  const absoluteEaches = getAbsoluteEachesByUomId(unitsOfMeasure, unitOfMeasureId);
  const baseQuantity = toBaseQuantity(quantity, absoluteEaches);
  const hasError = baseQuantity > available;

  if (isCollapsed) {
    return <Text textType={TextType.Paragraph} value={quantity.toString()} />;
  }

  const handleHalfClick = () => {
    onQuantityChange(productId, rowId, toUomQuantity(Math.floor(available / 2), absoluteEaches));
  };

  const handleAllClick = () => {
    onQuantityChange(productId, rowId, toUomQuantity(available, absoluteEaches));
  };

  const handleInputChange = (value: number | undefined) => {
    onQuantityChange(productId, rowId, value ?? 0);
  };

  return (
    <FlexColumn>
      <FlexRow className={styles['qty-cell']} gap={Gap.Space8}>
        <NumberInput
          className={`${styles['qty-input']} ${hasError ? styles['qty-input-error'] : ''}`}
          min={0}
          onChange={handleInputChange}
          value={quantity}
        />
        <Button className={styles['qty-button']} onClick={handleHalfClick} theme={THEME_CONSTANTS.secondary}>
          <Text textType={TextType.Caption} value={messages.half} />
        </Button>
        <Button className={styles['qty-button']} onClick={handleAllClick} theme={THEME_CONSTANTS.secondary}>
          <Text textType={TextType.Caption} value={messages.all} />
        </Button>
      </FlexRow>
      {hasError ? (
        <div className={styles['qty-error']}>
          <Text formattedMessageValues={{ available }} textType={TextType.Caption} value={messages.quantityExceedsAvailable} />
        </div>
      ) : null}
    </FlexColumn>
  );
};

const createOnHandColumnDefinition = (width = 80) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.onHand} />,
    accessor: 'onHand',
    Cell: OnHandCell,
    width,
  })();

const createAvailableColumnDefinition = (width = 80) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.available} />,
    accessor: 'available',
    Cell: AvailableCell,
    width,
  })();

const createQtyColumnDefinition = (onQuantityChange: (productId: string, rowId: string, quantity: number) => void, width = 200) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.qty} />,
    accessor: 'quantity',
    Cell: ({ row }: { row: ColumnRow }) => <QtyCell onQuantityChange={onQuantityChange} row={row} />,
    width,
  })();

const createActionsColumnDefinition = (onRemoveRow: (productId: string, rowId: string, isCollapsed?: boolean) => void, width = 80) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.actions} />,
    accessor: 'actions',
    Cell: ({ row }: { row: ColumnRow }) => <ActionsCell onRemoveRow={onRemoveRow} row={row} />,
    width,
    className: styles['justify-center'],
  })();

const createProductColumnWithCollapseDefinition = (
  openProductDetails: (productId: string) => void,
  onToggleCollapse: (productId: string) => void,
  width = 280,
) =>
  createColumnDefinitionBuilder({
    Header: <Text className={styles['product-column-header']} textType={TextType.Label} value={sharedMessages.product} />,
    accessor: 'product',
    Cell: ({ row }: { row: ColumnRow }) => (
      <ProductCellWithCollapse onToggleCollapse={onToggleCollapse} openProductDetails={openProductDetails} row={row} />
    ),
    width,
  })();

const createLocationColumnWithCollapseDefinition = (width = 180) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={sharedMessages.location} />,
    accessor: 'location',
    Cell: LocationCellWithCollapse,
    width,
  })();

const createLotIdColumnWithCollapseDefinition = () =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={sharedMessages.lotId} />,
    accessor: 'lotId',
    Cell: LotIdCellWithCollapse,
    width: 120,
  })();

const createExpirationDateColumnWithCollapseDefinition = () =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={sharedMessages.expirationDate} />,
    accessor: 'expirationDate',
    Cell: ExpirationDateCellWithCollapse,
    width: 120,
  })();

const createUomColumnDefinition = (onUomChange: (productId: string, rowId: string, unitOfMeasureId: number | null) => void, width = 150) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.unitOfMeasure} />,
    accessor: 'unitOfMeasureId',
    Cell: ({ row }: { row: ColumnRow }) => <UomSelectCell onUomChange={onUomChange} row={row} />,
    width,
  })();

const createEachesColumnDefinition = (width = 120) =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={messages.eaches} />,
    accessor: 'eaches',
    Cell: EachesCell,
    width,
  })();

type GetColumnsOptions = {
  showAdvancedColumns: boolean;
  isUomEnabled: boolean;
  onQuantityChange: (productId: string, rowId: string, quantity: number) => void;
  onUomChange: (productId: string, rowId: string, unitOfMeasureId: number | null) => void;
  onRemoveRow: (productId: string, rowId: string, isCollapsed?: boolean) => void;
  onToggleCollapse: (productId: string) => void;
  openProductDetails: (productId: string) => void;
};

export const getColumns = ({
  showAdvancedColumns,
  isUomEnabled,
  onQuantityChange,
  onUomChange,
  onRemoveRow,
  onToggleCollapse,
  openProductDetails,
}: GetColumnsOptions) => {
  const advancedColumns = showAdvancedColumns
    ? [createLotIdColumnWithCollapseDefinition(), createExpirationDateColumnWithCollapseDefinition()]
    : [];

  const uomColumns = isUomEnabled ? [createUomColumnDefinition(onUomChange, 130), createEachesColumnDefinition(90)] : [];

  return [
    createProductColumnWithCollapseDefinition(openProductDetails, onToggleCollapse, isUomEnabled ? 220 : 280),
    createLocationColumnWithCollapseDefinition(isUomEnabled ? 140 : 180),
    createOnHandColumnDefinition(90),
    createAvailableColumnDefinition(90),
    ...advancedColumns,
    ...uomColumns,
    createQtyColumnDefinition(onQuantityChange, isUomEnabled ? 160 : 200),
    createActionsColumnDefinition(onRemoveRow, isUomEnabled ? 70 : 80),
  ];
};
